/**
 * Global Toast — Provider + Viewport + useToast 훅.
 *
 * - 화면 하단 fixed, safe-area 보정.
 * - 기본 3000ms 자동 dismiss.
 * - 동시 다발 호출 시 위로 쌓임 (FIFO).
 * - 같은 메시지 빠른 중복 호출은 마지막 것으로 갱신.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import ToastMessage, { type ToastPurpose } from './ToastMessage';
import viewportStyles from './ToastViewport.module.css';

const DEFAULT_DURATION_MS = 3000;
const MAX_TOASTS = 3;

interface ToastItem {
  id: number;
  purpose: ToastPurpose;
  text: string;
  duration: number;
}

type ShowOptions = {
  duration?: number;
};

interface ToastApi {
  show: (purpose: ToastPurpose, text: string, opts?: ShowOptions) => void;
  info: (text: string, opts?: ShowOptions) => void;
  success: (text: string, opts?: ShowOptions) => void;
  warning: (text: string, opts?: ShowOptions) => void;
  error: (text: string, opts?: ShowOptions) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within <ToastProvider>');
  }
  return ctx;
}

interface ToastProviderProps {
  children: React.ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current.clear();
    setToasts([]);
  }, []);

  const show = useCallback(
    (purpose: ToastPurpose, text: string, opts?: ShowOptions) => {
      const id = ++idRef.current;
      const duration = opts?.duration ?? DEFAULT_DURATION_MS;
      setToasts((prev) => {
        const next = [...prev, { id, purpose, text, duration }];
        if (next.length <= MAX_TOASTS) return next;
        const overflow = next.slice(0, next.length - MAX_TOASTS);
        overflow.forEach((t) => {
          const timer = timersRef.current.get(t.id);
          if (timer) clearTimeout(timer);
          timersRef.current.delete(t.id);
        });
        return next.slice(-MAX_TOASTS);
      });
      const timer = setTimeout(() => dismiss(id), duration);
      timersRef.current.set(id, timer);
    },
    [dismiss],
  );

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      info: (text, opts) => show('info', text, opts),
      success: (text, opts) => show('success', text, opts),
      warning: (text, opts) => show('warning', text, opts),
      error: (text, opts) => show('error', text, opts),
      dismissAll,
    }),
    [show, dismissAll],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

interface ToastViewportProps {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}

function ToastViewport({ toasts, onDismiss }: ToastViewportProps) {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className={viewportStyles.viewport} aria-live="polite" aria-atomic="false">
      {toasts.map((t) => (
        <div key={t.id} className={viewportStyles.item}>
          <ToastMessage
            purpose={t.purpose}
            text={t.text}
            onClose={() => onDismiss(t.id)}
          />
        </div>
      ))}
    </div>,
    document.body,
  );
}
